import classNames from "classnames";
import { Form, Formik, FormikConfig } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { ConflictsResponse } from "./PublishedWorkflowTemplate";
import renameDialogStyle from "./rename-conflicting-workflows-dialog.scss";
import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { workflowService } from "services/workflows/WorkflowService";
import form from "styles/form.scss";
import { hasOnlyValidWorkflowCharacters } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    show: boolean;
    conflictsException: ConflictsResponse;
    onClose: () => void;
    onRename: (renameTargets: Record<string, string>) => void;
}

interface FormValues {
    renameTargets: Record<string, string>;
}

const RenameConflictingWorkflowsDialog = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);

    const initialValues: FormValues = React.useMemo(() => {
        const targets: Record<string, string> = {};

        if (props.conflictsException?.conflictingTemplate) {
            targets[props.conflictsException.conflictingTemplate] = "";
        }

        if (props.conflictsException?.conflictingSubWorkflows) {
            props.conflictsException.conflictingSubWorkflows.forEach((name) => {
                targets[name] = "";
            });
        }

        return { renameTargets: targets };
    }, [props.conflictsException]);

    const validationSchema = React.useMemo(() => {
        const conflictingNames: string[] = [];

        if (props.conflictsException?.conflictingTemplate) {
            conflictingNames.push(props.conflictsException.conflictingTemplate);
        }
        if (props.conflictsException?.conflictingSubWorkflows) {
            conflictingNames.push(...props.conflictsException.conflictingSubWorkflows);
        }

        const schemaFields: Record<string, yup.StringSchema> = {};

        conflictingNames.forEach((name) => {
            schemaFields[name] = yup
                .string()
                .required(t("WorkflowsTable.actions.create.validation.nameRequired"))
                .test(
                    "valid-characters",
                    t("WorkflowsTable.actions.create.validation.nameSpecialCharacters"),
                    (value) => {
                        return !value || hasOnlyValidWorkflowCharacters(value);
                    }
                );
        });

        return yup.object({
            renameTargets: yup.object(schemaFields),
        });
    }, [props.conflictsException, t]);

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (values, { setErrors, setSubmitting }) => {
        try {
            setLoading(true);

            const duplicateErrors: Record<string, string> = {};

            Object.entries(values.renameTargets).forEach(([originalName, newName]) => {
                const otherNames = Object.entries(values.renameTargets)
                    .filter(([key]) => key !== originalName)
                    .map(([, value]) => value);

                if (otherNames.includes(newName)) {
                    duplicateErrors[originalName] = t(
                        "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.duplicateNameError"
                    );
                }
            });

            if (Object.keys(duplicateErrors).length > 0) {
                setLoading(false);
                setErrors({ renameTargets: duplicateErrors });
                setSubmitting(false);
                return;
            }

            const namesToValidate = Object.values(values.renameTargets);
            const abortController = new AbortController();
            const availabilityMap = await workflowService.checkWorkflowNameAvailability(
                namesToValidate,
                abortController
            );

            const errors: Record<string, string> = {};
            Object.entries(values.renameTargets).forEach(([originalName, newName]) => {
                if (!availabilityMap.get(newName)) {
                    errors[originalName] = t("ManageWorkflowDialog.selectWorkflowEditor.workflowNameNotAvailable");
                }
            });

            if (Object.keys(errors).length > 0) {
                setLoading(false);
                setErrors({ renameTargets: errors });
                setSubmitting(false);
            } else {
                props.onRename(values.renameTargets);
            }
        } catch {
            setLoading(false);
            setSubmitting(false);
        } finally {
            setLoading(false);
        }
    };

    const templateWorkflow = props.conflictsException?.conflictingTemplate;
    const connectedWorkflows = props.conflictsException?.conflictingSubWorkflows || [];

    return (
        <Modal
            isOpen={props.show}
            hideModal={props.onClose}
            modalTitle={t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.title")}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={submitHandler}
                enableReinitialize
            >
                {({ values, errors, setFieldValue, setFieldError }) => (
                    <Form className={renameDialogStyle.renameDialog}>
                        <div className={renameDialogStyle.message}>
                            {t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.message")}
                        </div>

                        <div className={renameDialogStyle.scrollableContent}>
                            {templateWorkflow && (
                                <div className={renameDialogStyle.section}>
                                    <div className={renameDialogStyle.sectionTitle}>
                                        {t("WorkflowsTable.connectedWorkflows.template")}
                                    </div>
                                    <div className={renameDialogStyle.columnHeaders}>
                                        <div className={renameDialogStyle.headerLabel}>
                                            {t(
                                                "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.currentName"
                                            )}
                                        </div>
                                        <div className={renameDialogStyle.headerLabel}>
                                            {t(
                                                "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.newName"
                                            )}
                                        </div>
                                    </div>
                                    <div className={renameDialogStyle.nameRow}>
                                        <div
                                            className={renameDialogStyle.currentName}
                                            data-testid={
                                                testIds.workArea.workflowTemplates.renameConflictingWorkflowsDialog
                                                    .templateName.label
                                            }
                                        >
                                            {templateWorkflow}
                                        </div>
                                        <div className={form.formFieldsColumnDirection}>
                                            <input
                                                type="text"
                                                value={values.renameTargets[templateWorkflow] || ""}
                                                onChange={(e) => {
                                                    setFieldValue(`renameTargets.${templateWorkflow}`, e.target.value);
                                                    if (errors.renameTargets?.[templateWorkflow]) {
                                                        setFieldError(`renameTargets.${templateWorkflow}`, undefined);
                                                    }
                                                }}
                                                className={classNames(form.input, renameDialogStyle.newNameInput, {
                                                    [renameDialogStyle.inputError]:
                                                        errors.renameTargets?.[templateWorkflow],
                                                })}
                                                data-testid={
                                                    testIds.workArea.workflowTemplates.renameConflictingWorkflowsDialog
                                                        .templateName.input
                                                }
                                            />
                                            {loading && (
                                                <div className={renameDialogStyle.loaderContainer}>
                                                    <LoadingIndicator small={true} />
                                                </div>
                                            )}
                                            <div
                                                className={form.error}
                                                data-testid={
                                                    testIds.workArea.workflowTemplates.renameConflictingWorkflowsDialog
                                                        .templateName.errorLabel
                                                }
                                            >
                                                {errors.renameTargets?.[templateWorkflow] || ""}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {connectedWorkflows.length > 0 && (
                                <div className={renameDialogStyle.section}>
                                    <div className={renameDialogStyle.sectionTitle}>
                                        {t("WorkflowsTable.connectedWorkflows.title")}
                                    </div>
                                    <div className={renameDialogStyle.columnHeaders}>
                                        <div className={renameDialogStyle.headerLabel}>
                                            {t(
                                                "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.currentName"
                                            )}
                                        </div>
                                        <div className={renameDialogStyle.headerLabel}>
                                            {t(
                                                "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.newName"
                                            )}
                                        </div>
                                    </div>
                                    {connectedWorkflows.map((workflowName) => (
                                        <div
                                            key={workflowName}
                                            className={renameDialogStyle.nameRow}
                                            data-testid={
                                                testIds.workArea.workflowTemplates.renameConflictingWorkflowsDialog
                                                    .connectedWorkflowRow.itself
                                            }
                                        >
                                            <div
                                                className={renameDialogStyle.currentName}
                                                data-testid={
                                                    testIds.workArea.workflowTemplates.renameConflictingWorkflowsDialog
                                                        .connectedWorkflowRow.label
                                                }
                                            >
                                                {workflowName}
                                            </div>
                                            <div className={form.formFieldsColumnDirection}>
                                                <input
                                                    type="text"
                                                    value={values.renameTargets[workflowName] || ""}
                                                    onChange={(e) => {
                                                        setFieldValue(`renameTargets.${workflowName}`, e.target.value);
                                                        if (errors.renameTargets?.[workflowName]) {
                                                            setFieldError(`renameTargets.${workflowName}`, undefined);
                                                        }
                                                    }}
                                                    className={classNames(form.input, renameDialogStyle.newNameInput, {
                                                        [renameDialogStyle.inputError]:
                                                            errors.renameTargets?.[workflowName],
                                                    })}
                                                    data-testid={
                                                        testIds.workArea.workflowTemplates
                                                            .renameConflictingWorkflowsDialog.connectedWorkflowRow.input
                                                    }
                                                />
                                                {loading && (
                                                    <div className={renameDialogStyle.loaderContainer}>
                                                        <LoadingIndicator small={true} />
                                                    </div>
                                                )}
                                                <div
                                                    className={form.error}
                                                    data-testid={
                                                        testIds.workArea.workflowTemplates
                                                            .renameConflictingWorkflowsDialog.connectedWorkflowRow
                                                            .errorLabel
                                                    }
                                                >
                                                    {errors.renameTargets?.[workflowName] || ""}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <ButtonContainer>
                            <Button
                                type="submit"
                                variant="PRIMARY"
                                disabled={loading}
                                testId={testIds.common.confirmationDialog.confirmButton}
                            >
                                {t(
                                    "workflowTemplate.workflowTemplateDetailsDialog.error.conflict.renameDialog.confirmAndAdd"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="SECONDARY"
                                onClick={props.onClose}
                                testId={testIds.common.dialog.closeButton}
                            >
                                {t("Common.cancel")}
                            </Button>
                        </ButtonContainer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default RenameConflictingWorkflowsDialog;
